import api from '@/api';
import type { components } from '@/api/qraft/schema';

export type StudentAdoptionInfo = components['schemas']['StudentAdoptionInfo'];

/**
 * Which ladder the endpoint runs before it answers. Omitted means `adopt`, the
 * server's default; `assign` runs no adoption ladder (no helper is chosen yet)
 * but adds the coordinator gate and narrows the web targets to groups the
 * caller admins.
 */
export type AdoptionInfoMode = 'adopt' | 'assign';

export interface StudentAdoptionInfoResult {
  info: StudentAdoptionInfo | undefined;
  isLoading: boolean;
  /** A refusal, not a crash: see below. */
  error: unknown;
}

/**
 * The adopt / pass-on confirm payload for one student.
 *
 * The endpoint answers a typed 4xx instead of the payload when the caller may
 * not adopt, so `error` is as much a result as `data`, hence `retry: false`.
 *
 * @param studentId the student whose modal is open, or `null` when none is.
 *
 * @param enabled false once the action has succeeded: the student is off the
 * board by then, so a refetch would answer 404/409 and blank the success body.
 *
 * @param mode omitted for the adopt modal, `'assign'` for the assign one. Left
 * off the request when omitted, so the adopt modal's query key and URL are
 * unchanged.
 */
export function useStudentAdoptionInfo(
  studentId: number | null,
  enabled: boolean,
  mode?: AdoptionInfoMode,
): StudentAdoptionInfoResult {
  const path = { student_id: studentId ?? 0 };
  const { data, isLoading, error } = api.getStudentAdoptionInfo.useQuery(
    mode === undefined ? { path } : { path, query: { mode } },
    { enabled: enabled && studentId !== null, retry: false },
  );

  return { info: data, isLoading, error };
}
